import { forwardRef } from "react";
import { Input } from "@/components/ui/input";

/**
 * Numeric input that displays the value with non-breaking space thousand
 * separators ("1 000 000"), but emits a clean digit string through onChange.
 *
 * Usage:
 *   <NumericInput value={form.amount} onChange={(e) => setForm(... e.target.value ...)} />
 *
 * Internally uses type="text" + inputMode="numeric" so the browser allows
 * the formatted display (type="number" rejects non-digit chars including spaces).
 */
const cleanDigits = (s) => String(s ?? "").replace(/\D+/g, "");

const formatThousands = (digits) => {
  if (!digits) return "";
  // Non-breaking space (\u00a0) keeps the number from line-wrapping.
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0");
};

export const NumericInput = forwardRef(function NumericInput(
  { value, onChange, ...rest },
  ref
) {
  const display = formatThousands(cleanDigits(value));
  return (
    <Input
      ref={ref}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={display}
      onChange={(e) => {
        const digits = cleanDigits(e.target.value);
        if (onChange) onChange({ target: { value: digits } });
      }}
      {...rest}
    />
  );
});
